import { ArrowUpRight } from 'lucide-react';
import { connectorsPath } from '../../../lib/connectors-route';
import { useTranslation } from '../../../lib/i18n';
import { useBasePath } from '../../../lib/use-base-path';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useProductName } from '../../../lib/use-brand';
import { Card, TextButton } from '../ui';
import styles from './ConnectToolsCard.module.css';

// "Connect your tools to one Symphony" — the home's standing door into the Connectors page
// (Figma 15545:336821 phone / 15545:337754 desktop): a lime callout carrying the 3×3 cluster of app
// marks and an underlined "Connect tools ↗".
//
// WHERE IT SITS is decided by whether the account is also being offered the download-the-app QR
// (`useShowMobileAppCard`), because the two share the header's ⅓ column and only one can have it:
//
//   • QR shown    → the QR keeps the header slot and this card becomes the band's THIRD slot, to the
//                   right of the ideas pile (which is also what makes the tasks strip pile up — see
//                   `PiledUpJobs`'s `piled`).
//   • QR not shown → this card takes the header's ⅓ column, in the QR's place, and the band is left
//                   exactly as it was.
//
// `CatchUpSections` owns that choice for both the wide band and the stacked (phone / docked-chat)
// column; `DesktopHomeHeader` owns the header slot. Neither renders it twice — see the note there.
//
// Unlike the QR card this one has a NATIVE counterpart (iOS `ConnectToolsCard`, Android
// `ConnectToolsCard`), and all three read the SAME key names (`home.connectTools.*`) out of their own
// catalogs — so the three sentences can be diffed against each other rather than drifting quietly.

const PAGE = 'home';

export function ConnectToolsCard({ className = '' }: { className?: string } = {}) {
  const { t } = useTranslation();
  const basePath = useBasePath();
  const productName = useProductName();
  const { logPageClickBi } = useGenericBi();

  return (
    <Card className={`${styles.card} ${styles.lime} ${className}`.trim()}>
      <div className={styles.row}>
        <div className={styles.copy}>
          {/* Not a `Typography` variant: the DESKTOP design is the display face (Archivo) at 28/28, a
              step the Symphony scale doesn't carry, spelled out in the module against the Figma node
              (15545:338058). Desktop-only — mobile web keeps its shipped UI-face title. The WEIGHT
              rides the role token, so the #7882 weight split applies. */}
          <h2 className={styles.title}>{t('home.connectTools.title', { productName })}</h2>
          <TextButton
            size="small"
            underline
            className={styles.cta}
            href={connectorsPath({ tab: 'tools' })}
            endIcon={<ArrowUpRight size={16} className="dir-flip" aria-hidden />}
            onClick={() =>
              logPageClickBi({ page_name: PAGE, button_name: 'connect_tools' }, { componentName: 'connect-tools' })
            }
          >
            {t('home.connectTools.cta')}
          </TextButton>
        </div>
        {/* One flat artwork rather than nine brand SVGs assembled three times: the cluster is an
            illustration, not an icon system, and the same PNG is shipped to web, iOS and Android
            (`connect_tools_icons`) so the three cannot drift. It is TRANSPARENT in its gaps, so the
            card's own fill shows through them and the surface can flip with the theme (its one lime
            Symphony tile stays lime, as the Dark frame draws). Decorative — the sentence beside it
            already says what it means, so it is hidden from screen readers. */}
        <img src={`${basePath}/connect-tools-icons.png`} alt="" aria-hidden className={styles.icons} />
      </div>
    </Card>
  );
}
